/**
 * MasterLayout — purple/gold themed shell for the Super Admin section.
 *
 * Sidebar supports a real collapse (240px ↔ 72px) persisted in localStorage
 * so the operator keeps more horizontal room on dense screens.
 *
 * Iter120 — grouped navigation:
 *   • "Communication" folds Email Analytics + A/B Analytics.
 *   • "Settings" folds Demo Profiles + Gmail SMTP + Claire Insights +
 *     Packages + Agents.
 * Groups are expandable and their open/closed state persists in localStorage.
 *
 * Iter120 — Claire helper: header now hosts a "Claire" button that
 * dispatches the `claire:open` CustomEvent. The floating FAB is
 * suppressed inside /master/* (see ClaireWidget) so it no longer
 * overlaps sidebar navigation.
 */
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Crown, LayoutDashboard, Users, Package, Bot, FileText, LogOut,
  ArrowLeftRight, BarChart3, Sparkles, ChevronsLeft, ChevronsRight, Mail, UserCog,
  Send, Landmark, BookOpen, ChevronDown, ChevronRight, Settings, MessageSquare, Wand2,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import LangToggle from "../../components/LangToggle";
import PersonaSwitcher from "../../components/PersonaSwitcher";

/**
 * NAV supports flat items and grouped items (children).
 *  - Flat item: { id, to, icon, label }
 *  - Group:     { id, icon, label, group: true, children: [flat...] }
 */
const NAV = [
  { id: "dashboard", to: "/master",             icon: LayoutDashboard, label: "Dashboard" },
  { id: "tenants",   to: "/master/tenants",     icon: Users,           label: "Clients" },
  {
    id: "communication",
    group: true,
    icon: MessageSquare,
    label: "Communication",
    children: [
      { id: "email-analytics", to: "/master/email-analytics", icon: Send,      label: "Email Analytics" },
      { id: "analytics",       to: "/master/analytics",       icon: BarChart3, label: "A/B Analytics" },
    ],
  },
  { id: "procurement-pulse", to: "/master/procurement-pulse", icon: Landmark, label: "Procurement Pulse" },
  { id: "content",    to: "/master/content",     icon: Sparkles, label: "Content AI" },
  { id: "claire-kb",  to: "/master/claire-kb",   icon: Bot,      label: "Claire KB" },
  { id: "templates",  to: "/master/templates",   icon: BookOpen, label: "Templates" },
  {
    id: "settings",
    group: true,
    icon: Settings,
    label: "Settings",
    children: [
      { id: "demos",           to: "/master/demo-accounts",   icon: UserCog,   label: "Demo Profiles" },
      { id: "gmail",           to: "/master/gmail",           icon: Mail,      label: "Gmail SMTP" },
      { id: "claire-insights", to: "/master/claire-insights", icon: BarChart3, label: "Claire Insights" },
      { id: "packages",        to: "/master/packages",        icon: Package,   label: "Packages" },
      { id: "agents",          to: "/master/agents",          icon: Bot,       label: "Agents" },
      { id: "sectors",         to: "/master/sectors",         icon: Wand2,     label: "Sector Cleanup" },
    ],
  },
  { id: "logs", to: "/master/access-logs", icon: FileText, label: "Access Logs" },
];

const STORAGE_KEY = "alp.master.sidebar.collapsed";
const GROUPS_KEY  = "alp.master.sidebar.groups.v1";

function loadGroupsOpen() {
  try {
    const raw = localStorage.getItem(GROUPS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  // Default: both groups open on first paint.
  return { communication: true, settings: true };
}

export default function MasterLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch { /* noop */ }
  }, [collapsed]);

  const [groupsOpen, setGroupsOpen] = useState(() => loadGroupsOpen());
  useEffect(() => {
    try { localStorage.setItem(GROUPS_KEY, JSON.stringify(groupsOpen)); } catch { /* noop */ }
  }, [groupsOpen]);

  // Auto-open a group when the current path matches one of its children.
  useEffect(() => {
    setGroupsOpen((prev) => {
      const next = { ...prev };
      let changed = false;
      NAV.forEach((it) => {
        if (!it.group) return;
        const hit = it.children?.some((c) => location.pathname.startsWith(c.to));
        if (hit && !prev[it.id]) { next[it.id] = true; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  const W = collapsed ? 72 : 240;

  const isActive = (to) =>
    location.pathname === to || (to !== "/master" && location.pathname.startsWith(to));

  const openClaire = () => {
    try {
      window.dispatchEvent(new CustomEvent("claire:open", { detail: { prefill: "", autoSend: false } }));
    } catch { /* noop */ }
  };

  return (
    <div data-testid="master-layout" className="min-h-screen flex" style={{ background: "#0F0820" }}>
      {/* Sidebar */}
      <aside
        data-testid="master-sidebar"
        className="shrink-0 fixed inset-y-0 left-0 flex flex-col border-r transition-all duration-200"
        style={{
          width: W,
          background: "linear-gradient(180deg, #1A0E36 0%, #0F0820 100%)",
          borderColor: "rgba(201,162,78,0.18)",
        }}
      >
        <div className="p-4 border-b flex items-center justify-between gap-2"
          style={{ borderColor: "rgba(201,162,78,0.18)" }}>
          <div className="flex items-center gap-2 text-[#C9A24E] min-w-0">
            <Crown className="w-5 h-5 shrink-0" />
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-extrabold text-[14px] tracking-tight text-white truncate">Master Console</div>
                <div className="text-[9.5px] text-white/50 tracking-[0.18em] uppercase font-semibold truncate">
                  AgenteLead Pro
                </div>
              </div>
            )}
          </div>
          <button
            data-testid="master-sidebar-toggle"
            onClick={() => setCollapsed((c) => !c)}
            className="p-1 rounded hover:bg-white/10 text-white/55 hover:text-white transition-colors shrink-0"
            title={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        </div>

        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {NAV.map((it) => {
            if (it.group) {
              const Icon = it.icon;
              const open = !!groupsOpen[it.id];
              const anyActive = it.children?.some((c) => isActive(c.to));
              return (
                <div key={it.id} className="space-y-1">
                  <button
                    data-testid={`master-nav-group-${it.id}`}
                    onClick={() => {
                      if (collapsed) { setCollapsed(false); setGroupsOpen((g) => ({ ...g, [it.id]: true })); return; }
                      setGroupsOpen((g) => ({ ...g, [it.id]: !g[it.id] }));
                    }}
                    title={collapsed ? it.label : undefined}
                    className={`w-full flex items-center gap-3 rounded-lg text-[12px] uppercase tracking-wider font-bold transition-all ${
                      collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2"
                    } ${
                      anyActive
                        ? "text-[#C9A24E]"
                        : "text-white/50 hover:text-white/80"
                    }`}
                  >
                    <Icon className="w-[16px] h-[16px] shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left truncate">{it.label}</span>
                        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </>
                    )}
                  </button>
                  {!collapsed && open && (
                    <div className="ml-2 pl-2 border-l space-y-1"
                      style={{ borderColor: "rgba(201,162,78,0.18)" }}>
                      {it.children.map((c) => {
                        const CIcon = c.icon;
                        const active = isActive(c.to);
                        return (
                          <button
                            key={c.id}
                            data-testid={`master-nav-${c.id}`}
                            onClick={() => navigate(c.to)}
                            className={`w-full flex items-center gap-3 rounded-lg text-[13px] px-3 py-2 transition-all ${
                              active
                                ? "bg-[#C9A24E] text-[#0F0820] font-bold shadow-lg"
                                : "text-white/75 hover:bg-white/5 hover:text-white"
                            }`}
                          >
                            <CIcon className="w-[15px] h-[15px] shrink-0" />
                            <span className="truncate">{c.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {collapsed && (
                    <div className="space-y-1">
                      {it.children.map((c) => {
                        const CIcon = c.icon;
                        const active = isActive(c.to);
                        return (
                          <button
                            key={c.id}
                            data-testid={`master-nav-${c.id}`}
                            onClick={() => navigate(c.to)}
                            title={c.label}
                            className={`w-full flex items-center justify-center rounded-lg text-[13px] px-2 py-2.5 transition-all ${
                              active
                                ? "bg-[#C9A24E] text-[#0F0820] font-bold shadow-lg"
                                : "text-white/75 hover:bg-white/5 hover:text-white"
                            }`}
                          >
                            <CIcon className="w-[16px] h-[16px] shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const Icon = it.icon;
            const active = isActive(it.to);
            return (
              <button
                key={it.id}
                data-testid={`master-nav-${it.id}`}
                onClick={() => navigate(it.to)}
                title={collapsed ? it.label : undefined}
                className={`w-full flex items-center gap-3 rounded-lg text-[13px] transition-all ${
                  collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
                } ${
                  active
                    ? "bg-[#C9A24E] text-[#0F0820] font-bold shadow-lg"
                    : "text-white/75 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="w-[16px] h-[16px] shrink-0" />
                {!collapsed && <span className="truncate">{it.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="p-2 border-t space-y-1" style={{ borderColor: "rgba(201,162,78,0.18)" }}>
          <Link
            to="/dashboard"
            data-testid="master-back-to-app"
            title={collapsed ? "Switch to Client App" : undefined}
            className={`w-full flex items-center gap-3 rounded-lg text-[12px] text-white/65 hover:bg-white/5 hover:text-white transition-all ${
              collapsed ? "justify-center px-2 py-2" : "px-3 py-2"
            }`}
          >
            <ArrowLeftRight className="w-[14px] h-[14px] shrink-0" />
            {!collapsed && <span className="truncate">Switch to Client App</span>}
          </Link>
          <button
            data-testid="master-logout"
            onClick={async () => { await logout(); navigate("/login"); }}
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center gap-3 rounded-lg text-[12px] text-white/65 hover:bg-red-500/15 hover:text-red-300 transition-all ${
              collapsed ? "justify-center px-2 py-2" : "px-3 py-2"
            }`}
          >
            <LogOut className="w-[14px] h-[14px] shrink-0" />
            {!collapsed && <span className="truncate">Sign out</span>}
          </button>
          {!collapsed && (
            <div className="text-[10px] text-white/35 px-2 pt-1 truncate">{user?.email}</div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-all duration-200"
        style={{ marginLeft: W }}
      >
        <header className="h-14 border-b flex items-center justify-between px-6 sticky top-0 z-20"
          style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(15,8,32,0.85)", backdropFilter: "blur(8px)" }}>
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#C9A24E]" />
            <h1 className="text-white font-bold text-[14px]">{title || "Master Console"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openClaire}
              data-testid="master-header-claire-btn"
              title="Falar com a Claire"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold text-white bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] hover:from-[#6D28D9] hover:to-[#4338CA] shadow-sm transition-colors ring-1 ring-white/10"
            >
              <span className="relative inline-flex">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 ring-1 ring-[#4F46E5] animate-pulse" />
              </span>
              Claire
            </button>
            <PersonaSwitcher />
            <LangToggle />
          </div>
        </header>

        <main className="flex-1 p-6 text-white">{children}</main>
      </div>
    </div>
  );
}
